import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { RHFInput } from '@/Components/shadcn/rhf-input';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/Components/shadcn/dialog';
import { TypographyP } from '@/Components/shadcn';
import { Button } from '@/Components/shadcn/button';
import { Spinner } from '@/Components/shadcn/spinner';
import { NewTeamMember, newTeamMemberSchema } from '@/Models/TeamMember';
import UIStrings from '@/Utils/UIStrings';
import TeamMemberRolePicker from '../TeamMembersRolePicker';
import { useTeamMembersContext } from '../Context/TeamMembersContext';
import { useNotificationContext } from '@/Context/NotificationContext';
import MembershipExistsError from '@/Utils/HTTP/Errors/MembershipExistsError';
import InvalidDomainError from '@/Utils/HTTP/Errors/InvalidDomainError';
import InvitationExistsError from '@/Utils/HTTP/Errors/InvitationExistsError';

interface TeamMemberAddDialogProps {
    open: boolean;
    onClose: () => void;
}

const TeamMemberAddDialog = (props: TeamMemberAddDialogProps) => {
    const { sendInvitation, isOperationLoading } = useTeamMembersContext();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const form = useForm({
        resolver: yupResolver(newTeamMemberSchema),
        mode: 'onSubmit',
        defaultValues: { email: '', role: 'Admin' } as NewTeamMember,
    });

    const closeDialog = () => {
        props.onClose();
        form.reset();
    };

    const saveNewTeamMember = async (values: NewTeamMember) => {
        try {
            const { email, role } = values;
            await sendInvitation(email, role);
            closeDialog();
            showSuccessToaster(UIStrings.TheInvitationHasBeenSendTo(email));
        } catch (error) {
            if (
                error instanceof MembershipExistsError ||
                error instanceof InvalidDomainError ||
                error instanceof InvitationExistsError
            ) {
                const { detail } = error;
                form.setError('email', { type: 'custom', message: detail });
            } else {
                showFailToaster();
            }
        }
    };

    return (
        <Dialog open={props.open} onOpenChange={(isOpen) => !isOpen && closeDialog()}>
            <DialogContent className="max-w-4xl">
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(saveNewTeamMember)}>
                        <DialogHeader>
                            <DialogTitle className="text-primary pb-2.5">{UIStrings.InviteNewTeamMember}</DialogTitle>
                        </DialogHeader>
                        <RHFInput
                            label={`${UIStrings.Email}*`}
                            name="email"
                            fullWidth
                            fieldDataCy="email"
                            fieldClassName="mb-5"
                        />
                        <TeamMemberRolePicker />
                        {form.formState.errors.role?.message && (
                            <TypographyP className="text-destructive mt-1">
                                {form.formState.errors.role.message}
                            </TypographyP>
                        )}
                        <DialogFooter className="mt-10 flex gap-2">
                            <Button type="button" variant="outline" onClick={closeDialog}>
                                {UIStrings.Cancel}
                            </Button>
                            <Button type="submit" disabled={!form.formState.isDirty || isOperationLoading}>
                                {isOperationLoading && <Spinner className="mr-2 size-4" />}
                                {UIStrings.SendInvitation}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default TeamMemberAddDialog;
